import type { MetadataJson } from 'libphonenumber-js';
import { parsePhoneNumberFromString } from 'libphonenumber-js/core';

// Local subset of libphonenumber metadata (only the countries we need)
import _metadata from './metadata.min.json';

const metadata = _metadata as unknown as MetadataJson;

/**
 * E.164 prefixes for the supported markets.
 */
enum PhoneNumberUnit {
  Sweden = '+46',
  Norway = '+47',
}

export type SupportedCountry = 'SE' | 'NO';

const CountryCodeMapper: Record<PhoneNumberUnit, SupportedCountry> = {
  [PhoneNumberUnit.Sweden]: 'SE',
  [PhoneNumberUnit.Norway]: 'NO',
};

const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  CountryCodeMapper[PhoneNumberUnit.Sweden],
  CountryCodeMapper[PhoneNumberUnit.Norway],
];

/**
 * Thin wrapper that attempts to parse the input against the two supported
 * markets, falling back to the other one if the first attempt fails. This
 * makes it possible to handle numbers entered **with** or **without** an
 * explicit country prefix.
 */
const parseSupportedNumber = (rawInput: string, preferredCountry?: SupportedCountry) => {
  const input = rawInput.trim();

  // 1. Try parsing assuming the user already included the “+46/ +47” prefix.
  let phone = parsePhoneNumberFromString(input, metadata);
  if (phone?.isValid() && SUPPORTED_COUNTRIES.includes(phone.country as SupportedCountry)) {
    return phone;
  }

  // 2. Build the fallback order — preferred country first, then the rest.
  const fallbackOrder: SupportedCountry[] = preferredCountry
    ? [preferredCountry, ...SUPPORTED_COUNTRIES.filter((c) => c !== preferredCountry)]
    : SUPPORTED_COUNTRIES;

  for (const country of fallbackOrder) {
    phone = parsePhoneNumberFromString(input, country, metadata);
    if (phone?.isValid() && SUPPORTED_COUNTRIES.includes(phone.country as SupportedCountry)) {
      return phone;
    }
  }

  return undefined;
};

/**
 * Returns `true` if the supplied string is a syntactically valid Swedish **or**
 * Norwegian phone number.
 */
export const phoneNumberIsValid = (
  phoneNumber: string,
  preferredCountry?: SupportedCountry,
): boolean => {
  const parsed = parseSupportedNumber(phoneNumber, preferredCountry);
  return Boolean(parsed?.isValid());
};

/**
 * Formats a Swedish/Norwegian phone number to strict E.164 (e.g. “+46701234567”).
 * If the number is invalid or belongs to an unsupported market the function
 * returns `null`.
 */
export const formatPhoneNumber = (
  phoneNumber: string,
  preferredCountry?: SupportedCountry,
): string | null => {
  const parsed = parseSupportedNumber(phoneNumber, preferredCountry);
  return parsed ? parsed.format('E.164') : null;
};

/**
 * Pretty‑formats the number for the user. The style automatically adapts to
 * the detected market:
 *   🇸🇪 070‑123 45 67   (national SE format)
 *   🇳🇴 412 34 567     (national NO format)
 *
 * When the number cannot be parsed the function returns `null`.
 */
export const formatPhoneNumberPretty = (
  phoneNumber: string,
  style: 'NATIONAL' | 'INTERNATIONAL' = 'NATIONAL',
  preferredCountry?: SupportedCountry,
): string | null => {
  const parsed = parseSupportedNumber(phoneNumber, preferredCountry);
  return parsed ? parsed.format(style) : null;
};
